import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApolloClient, useMutation } from '@apollo/client';
import { ArrowPathIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CheckIcon, EyeIcon } from '@heroicons/react/24/solid';
import { StarIcon as SolidStarIcon } from '@heroicons/react/24/solid';
import { toast } from 'react-toastify';
import Button from '../components/common/button.tsx';
import InlineFormattedText from '../components/common/inline-formatted-text.tsx';
import Loading from '../components/common/loading.tsx';
import LocalDate from '../components/common/local-date.tsx';
import {
  cn,
  getCollapsedQuestionTextLines,
  getCollapsedSegments,
  getCollapsedSegmentsContinuation,
} from '../utils/common-utils.ts';
import { capitalizeWords } from '../utils/capitalize-words.ts';
import {
  GET_QUESTION_LOG_LIST,
  GetQuestionLogListQuery,
  GetQuestionLogListQueryVariables,
  MARK_QUESTION_LOG_OCCURRENCE_REVIEWED,
  MARK_QUESTION_LOG_OCCURRENCE_UNREVIEWED,
  MARK_QUESTION_LOG_REVIEWED,
  MARK_QUESTION_LOG_UNREVIEWED,
  QUESTION_LOG_REVIEW_STATUS,
  QuestionLogEntry,
  QuestionLogFilterInput,
  QuestionLogQuestion,
  QuestionLogTaxonomyFilter,
  TOGGLE_QUESTION_LOG_MARKED_FOR_REVIEW,
  TOGGLE_QUESTION_LOG_STAR,
} from '../apollo/question-log.ts';
import { QuestionLogFilterModal } from '../components/question-log/question-log-filter-modal.tsx';
import { QuestionLogMarkedForReviewButton } from '../components/question-log/question-log-marked-for-review-button.tsx';
import { QuestionLogReviewModal } from '../components/question-log/question-log-review-modal.tsx';
import { QuestionLogStarButton } from '../components/question-log/question-log-star-button.tsx';
import ForReviewFlagIcon from '../components/common/icons/for-review-flag-icon.tsx';
import {
  clearQuestionLogStatusPatch,
  clearQuestionLogStatusPatchFields,
  getQuestionLogStatusPatches,
  subscribeQuestionLogStatusPatched,
} from '../utils/question-log-status-sync.ts';

const QUESTION_LOG_PAGE_SIZE = 100;
const QUESTION_LOG_AUTOLOAD_PAGE_LIMIT = 10;

const scopeOptions = [
  { key: 'all', label: 'All' },
  {
    key: QUESTION_LOG_REVIEW_STATUS.Unreviewed,
    label: 'Unreviewed',
  },
  {
    key: QUESTION_LOG_REVIEW_STATUS.Reviewed,
    label: 'Reviewed',
  },
  { key: 'markedForReview', label: 'For Review' },
  { key: 'starred', label: 'Starred' },
] as const;

const SECTION_DISPLAY_ORDER = {
  'reading and writing': 0,
  math: 1,
} as const;

const SECTION_COLOR_THEME = {
  'reading and writing': {
    chipColor: 'blue' as const,
    cardSelected: 'border-blue bg-light-blue text-dark-blue',
    cardDefault:
      'border-table-border bg-white text-dark-blue hover:border-blue hover:bg-light-blue',
    rowUnreviewed: 'border-table-border bg-white',
    previewTitle: 'text-dark-blue',
    previewSubtitle: 'text-gray',
    groupTitle: 'text-dark-blue',
  },
  math: {
    chipColor: 'red' as const,
    cardSelected: 'border-[#D9584F] bg-[#FFF1EF] text-[#B9382F]',
    cardDefault:
      'border-[#F0C7C3] bg-white text-[#B9382F] hover:border-[#D9584F] hover:bg-[#FFF1EF]',
    rowUnreviewed: 'border-table-border bg-white',
    previewTitle: 'text-[#B9382F]',
    previewSubtitle: 'text-[#96635D]',
    groupTitle: 'text-[#B9382F]',
  },
  default: {
    chipColor: 'blue' as const,
    cardSelected: 'border-blue bg-light-blue text-dark-blue',
    cardDefault:
      'border-table-border bg-white text-dark-blue hover:border-blue hover:bg-light-blue',
    rowUnreviewed: 'border-table-border bg-white',
    previewTitle: 'text-dark-blue',
    previewSubtitle: 'text-gray',
    groupTitle: 'text-dark-blue',
  },
} as const;

type ScopeKey = (typeof scopeOptions)[number]['key'];
type QuestionLogEntryOverride = Partial<
  Pick<
    QuestionLogEntry,
    | 'reviewStatus'
    | 'reviewedAt'
    | 'starred'
    | 'starredAt'
    | 'markedForReview'
    | 'markedForReviewAt'
  >
>;
type QuestionLogQuickFilter = {
  sectionId?: number;
  domainId?: number;
  skillId?: number;
  qtypeId?: number;
};

type QuestionLogGroup = {
  key: string;
  title: string;
  items: QuestionLogEntry[];
};

const getQuestionLogEntryCacheKey = (entry: QuestionLogEntry) =>
  entry.itemKey;

const mergeQuestionLogEntries = (entries: QuestionLogEntry[]) =>
  Array.from(
    entries.reduce(
      (map, entry) =>
        map.set(getQuestionLogEntryCacheKey(entry), entry),
      new Map<string, QuestionLogEntry>(),
    ).values(),
  );

const normalizeSectionName = (name?: string | null) => name?.toLowerCase().trim();

const getSectionThemeKey = (name?: string | null) => {
  const normalizedName = normalizeSectionName(name);

  if (normalizedName === 'reading and writing') {
    return 'reading and writing' as const;
  }

  if (normalizedName === 'math') {
    return 'math' as const;
  }

  return 'default' as const;
};

const getSectionTheme = (name?: string | null) =>
  SECTION_COLOR_THEME[getSectionThemeKey(name)];

const formatAttemptOrdinal = (value: number) => {
  const remainderTen = value % 10;
  const remainderHundred = value % 100;

  if (remainderTen === 1 && remainderHundred !== 11) {
    return `${value}st Attempt`;
  }

  if (remainderTen === 2 && remainderHundred !== 12) {
    return `${value}nd Attempt`;
  }

  if (remainderTen === 3 && remainderHundred !== 13) {
    return `${value}rd Attempt`;
  }

  return `${value}th Attempt`;
};

const questionLogReviewActionClass =
  'min-w-[6rem] justify-center px-2.5 py-1.5 text-sm';

const questionLogDoneActionClass =
  'inline-flex items-center gap-1 whitespace-nowrap px-0 py-1 text-xs font-medium text-[#697586] transition-colors hover:underline hover:underline-offset-2 disabled:cursor-not-allowed disabled:text-gray disabled:no-underline disabled:opacity-60';

const questionLogDoneStateClass =
  'inline-flex items-center gap-1 rounded-full bg-[#E5F6EA] px-2 py-1 text-xs font-semibold text-[#1F7A3D]';

const questionLogGroupActionClass =
  'shrink-0 whitespace-nowrap px-0 py-1 text-xs font-medium text-[#697586] transition-colors hover:underline hover:underline-offset-2 disabled:cursor-not-allowed disabled:text-gray disabled:no-underline disabled:opacity-60';

const getQuestionLogChipColorClass = (
  _color: 'green' | 'orange' | 'blue' | 'red' = 'blue',
) => {
  void _color;

  return 'border border-[#E6EAF0] bg-[#FAFBFC] text-[#697586]';
};

const applyScopeToEntries = (
  entries: QuestionLogEntry[],
  scope: ScopeKey,
) => {
  switch (scope) {
    case QUESTION_LOG_REVIEW_STATUS.Unreviewed:
      return entries.filter(
        (entry) =>
          entry.reviewStatus === QUESTION_LOG_REVIEW_STATUS.Unreviewed,
      );
    case QUESTION_LOG_REVIEW_STATUS.Reviewed:
      return entries.filter(
        (entry) => entry.reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed,
      );
    case 'markedForReview':
      return entries.filter((entry) => entry.markedForReview);
    case 'starred':
      return entries.filter((entry) => entry.starred);
    default:
      return entries;
  }
};

const applyTaxonomyFilter = (
  entries: QuestionLogEntry[],
  filter: QuestionLogTaxonomyFilter,
) =>
  entries.filter((entry) => {
    if (
      filter.sectionIds?.length &&
      !filter.sectionIds.includes(entry.question.section?.id ?? -1)
    ) {
      return false;
    }

    if (
      filter.domainIds?.length &&
      !filter.domainIds.includes(entry.question.domain?.id ?? -1)
    ) {
      return false;
    }

    if (
      filter.skillIds?.length &&
      !filter.skillIds.includes(entry.question.skill?.id ?? -1)
    ) {
      return false;
    }

    if (filter.qtypeIds?.length) {
      const qtypeIds = [entry.question.type1?.id, entry.question.type2?.id].filter(
        (id): id is number => typeof id === 'number',
      );

      if (!qtypeIds.some((id) => filter.qtypeIds?.includes(id))) {
        return false;
      }
    }

    return true;
  });

const isVisibleQuestionLogEntry = (entry: QuestionLogEntry) =>
  entry.wrongCount > 0 || entry.starred || entry.markedForReview;

const sortSections = (
  sections: Array<{ id: number; name: string; count: number }>,
) =>
  sections.sort((a, b) => {
    const aOrder =
      SECTION_DISPLAY_ORDER[
        normalizeSectionName(a.name) as keyof typeof SECTION_DISPLAY_ORDER
      ] ?? 99;
    const bOrder =
      SECTION_DISPLAY_ORDER[
        normalizeSectionName(b.name) as keyof typeof SECTION_DISPLAY_ORDER
      ] ?? 99;

    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }

    return a.name.localeCompare(b.name);
  });

const renderScopeIcon = (scopeKey: ScopeKey) => {
  if (scopeKey === 'markedForReview') {
    return (
      <ForReviewFlagIcon
        className="h-4 w-4"
        fillColor="#C93A4D"
        strokeColor="#1F1F1F"
      />
    );
  }

  if (scopeKey === 'starred') {
    return <SolidStarIcon className="h-4 w-4 text-[#EDB21A]" />;
  }

  return null;
};

const getQuickFilterButtonClass = (selected: boolean) =>
  cn(
    'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium leading-5 transition-colors',
    selected
      ? 'border-blue bg-blue text-white'
      : 'border-[#D7E3F2] bg-white text-dark-blue hover:border-blue hover:bg-light-blue',
  );

const QuestionLogPreview = memo(({
  onClick,
  question,
}: {
  onClick?: () => void;
  question: QuestionLogQuestion;
}) => {
  const sectionName = normalizeSectionName(question.section?.name);
  const sectionTheme = getSectionTheme(question.section?.name);
  const isMath = sectionName === 'math';
  const mathPromptLines = getCollapsedQuestionTextLines(
    question.prompt,
    [104, 80],
    { appendEllipsis: false },
  );
  const promptSegments = getCollapsedSegments(question.prompt, 88);
  const promptContinuationSegments = getCollapsedSegmentsContinuation(
    question.prompt,
    88,
    72,
  );

  const collapsedContentLines = getCollapsedQuestionTextLines(
    question.content,
    [88],
  );
  const hasCollapsedContent =
    Boolean(collapsedContentLines[0]?.trim()) &&
    !/^no info\b/i.test(collapsedContentLines[0].trim());
  const contentSegments = getCollapsedSegments(question.content, 88);
  const topLineSegments = hasCollapsedContent
    ? contentSegments
    : promptSegments.length
      ? promptSegments
      : [];
  const bottomLineSegments = isMath
    ? promptContinuationSegments
    : hasCollapsedContent
      ? getCollapsedSegments(question.prompt, 88)
      : promptContinuationSegments;

  const previewContent = (
    <div className="min-w-0 w-full">
      <div
        className={cn(
          'overflow-hidden whitespace-nowrap text-ellipsis text-[15px] leading-snug',
          sectionTheme.previewTitle,
        )}
      >
        {isMath ? (
          mathPromptLines[0] || `Question #${question.originalId}`
        ) : topLineSegments.length ? (
          <InlineFormattedText segments={topLineSegments} />
        ) : (
          `Question #${question.originalId}`
        )}
      </div>
      {(isMath || !!bottomLineSegments.length) && (
        <div
          className={cn(
            'mt-0.5 overflow-hidden whitespace-nowrap text-ellipsis text-[15px] leading-snug',
            sectionTheme.previewSubtitle,
          )}
        >
          {isMath ? (
            mathPromptLines[1] || <span>&nbsp;</span>
          ) : bottomLineSegments.length ? (
            <InlineFormattedText segments={bottomLineSegments} />
          ) : (
            <span>&nbsp;</span>
          )}
        </div>
      )}
    </div>
  );

  if (!onClick) {
    return previewContent;
  }

  return (
    <button
      type="button"
      className="min-w-0 flex-1 overflow-hidden rounded-md px-0 py-0.5 text-left transition-colors hover:bg-[#EEF4FB]"
      onClick={onClick}
    >
      {previewContent}
    </button>
  );
});

QuestionLogPreview.displayName = 'QuestionLogPreview';

const QuestionLogFilterChip = ({
  title,
  color,
  onClick,
}: {
  title: string;
  color?: 'green' | 'orange' | 'blue' | 'red';
  onClick?: () => void;
}) => {
  const chipClassName = cn(
    'rounded px-1.5 py-0.5 text-[11px] font-medium leading-4 transition-colors',
    getQuestionLogChipColorClass(color),
  );

  if (!onClick) {
    return <span className={chipClassName}>{title}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(chipClassName, 'hover:brightness-95')}
    >
      {title}
    </button>
  );
};

const QuestionLogSelectedFilterChip = ({
  color = 'blue',
  onRemove,
  title,
}: {
  color?: 'green' | 'orange' | 'blue' | 'red';
  onRemove: () => void;
  title: string;
}) => (
  <button
    type="button"
    onClick={onRemove}
    className={cn(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium leading-4 transition-colors hover:brightness-95',
      getQuestionLogChipColorClass(color),
    )}
  >
    <span>{title}</span>
    <XMarkIcon className="h-3 w-3" />
  </button>
);

const QuestionLogEntryRow = memo(
  ({
    entry,
    attemptLabel,
    isSaving,
    onToggleMarkedForReview,
    onToggleStar,
    onReview,
    onOpenReview,
    onApplyQuickFilter,
  }: {
    entry: QuestionLogEntry;
    attemptLabel?: string;
    isSaving: boolean;
    onToggleMarkedForReview: (entry: QuestionLogEntry) => void;
    onToggleStar: (entry: QuestionLogEntry) => void;
    onReview: (entry: QuestionLogEntry) => void;
    onOpenReview: (itemKey: string) => void;
    onApplyQuickFilter: (filter: QuestionLogQuickFilter) => void;
  }) => {
    const sectionTheme = getSectionTheme(entry.question.section?.name);
    const qtypes = useMemo(
      () =>
        [entry.question.type1, entry.question.type2].filter(
          (item, index, array) =>
            item?.id &&
            array.findIndex((current) => current?.id === item.id) === index,
        ),
      [entry.question.type1, entry.question.type2],
    );

    const isReviewed =
      entry.reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed;

    return (
      <div
        className={cn(
          'rounded-xl border px-2.5 py-1.5 transition-colors',
          isReviewed
            ? 'border-[#D8EBDC] bg-[#FBFFFC]'
            : sectionTheme.rowUnreviewed,
        )}
      >
        <div className="flex flex-col gap-1.5 lg:grid lg:grid-cols-[minmax(0,1fr)_auto] lg:items-start lg:gap-2.5">
          <div className="flex min-w-0 gap-1.5">
            <div className="flex shrink-0 flex-col items-center gap-0 pt-0.5">
              <QuestionLogMarkedForReviewButton
                markedForReview={entry.markedForReview}
                disabled={isSaving}
                className="shrink-0"
                compact
                onClick={() => onToggleMarkedForReview(entry)}
              />
              <QuestionLogStarButton
                starred={entry.starred}
                disabled={isSaving}
                className="shrink-0"
                compact
                onClick={() => onToggleStar(entry)}
              />
            </div>

            <div className="min-w-0 flex-1">
              <QuestionLogPreview
                onClick={() => onOpenReview(entry.itemKey)}
                question={entry.question}
              />

              <div className="mt-0.5 flex flex-wrap gap-1">
                {entry.question.section?.name && (
                  <QuestionLogFilterChip
                    title={capitalizeWords(entry.question.section.name)}
                    color={sectionTheme.chipColor}
                    onClick={() =>
                      onApplyQuickFilter({
                        sectionId: entry.question.section?.id,
                      })
                    }
                  />
                )}
                {entry.question.domain?.name && (
                  <QuestionLogFilterChip
                    title={capitalizeWords(entry.question.domain.name)}
                    color="orange"
                    onClick={() =>
                      onApplyQuickFilter({
                        sectionId: entry.question.section?.id,
                        domainId: entry.question.domain?.id,
                      })
                    }
                  />
                )}
                {entry.question.skill?.name && (
                  <QuestionLogFilterChip
                    title={capitalizeWords(entry.question.skill.name)}
                    onClick={() =>
                      onApplyQuickFilter({
                        sectionId: entry.question.section?.id,
                        domainId: entry.question.domain?.id,
                        skillId: entry.question.skill?.id,
                      })
                    }
                  />
                )}
                {qtypes.map((type) => (
                  <QuestionLogFilterChip
                    key={type!.id}
                    title={capitalizeWords(type!.name)}
                    onClick={() =>
                      onApplyQuickFilter({
                        sectionId: entry.question.section?.id,
                        domainId: entry.question.domain?.id,
                        skillId: entry.question.skill?.id,
                        qtypeId: type?.id,
                      })
                    }
                  />
                ))}
                {entry.markedForReview && (
                  <QuestionLogFilterChip
                    title="For Review"
                    color="orange"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 lg:justify-self-end lg:pl-3">
            {isReviewed ? (
              <div className={questionLogDoneStateClass}>
                <CheckIcon className="h-3.5 w-3.5" />
                <span>Done</span>
              </div>
            ) : (
              <button
                type="button"
                disabled={isSaving}
                className={questionLogDoneActionClass}
                onClick={() => onReview(entry)}
              >
                <span>Mark as Done</span>
              </button>
            )}
            <Button
              className={questionLogReviewActionClass}
              icon={<EyeIcon className="ml-2 h-4 w-4" />}
              onClick={() => onOpenReview(entry.itemKey)}
            >
              Review
            </Button>
            {isSaving && (
              <div className="w-full text-right text-xs font-medium text-gray">
                Saving...
              </div>
            )}
            {attemptLabel && (
              <div className="w-full text-right text-xs font-medium text-gray">
                {attemptLabel}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
  (previousProps, nextProps) =>
    previousProps.entry === nextProps.entry &&
    previousProps.isSaving === nextProps.isSaving &&
    previousProps.attemptLabel === nextProps.attemptLabel,
);

QuestionLogEntryRow.displayName = 'QuestionLogEntryRow';

const QuestionsLog = () => {
  const client = useApolloClient();
  const [scope, setScope] = useState<ScopeKey>('all');
  const [taxonomyFilter, setTaxonomyFilter] =
    useState<QuestionLogTaxonomyFilter>({});
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [activeItemKey, setActiveItemKey] = useState<string>();
  const [reviewQueueEntries, setReviewQueueEntries] = useState<QuestionLogEntry[]>(
    [],
  );
  const [serverEntries, setServerEntries] = useState<QuestionLogEntry[]>([]);
  const [isQuestionLogLoading, setIsQuestionLogLoading] = useState(true);
  const [isQuestionLogRefreshing, setIsQuestionLogRefreshing] = useState(false);
  const [loadedPageCount, setLoadedPageCount] = useState(0);
  const [totalPageCount, setTotalPageCount] = useState(0);
  const [pendingItemKeys, setPendingItemKeys] = useState<string[]>([]);
  const [pendingQuestionIds, setPendingQuestionIds] = useState<number[]>([]);
  const [entryOverrides, setEntryOverrides] = useState<
    Record<string, QuestionLogEntryOverride>
  >({});
  const [externalQuestionOverrides, setExternalQuestionOverrides] = useState<
    Record<number, QuestionLogEntryOverride>
  >(() =>
    Object.fromEntries(
      getQuestionLogStatusPatches().map(({ questionId, ...patch }) => [
        questionId,
        patch,
      ]),
    ),
  );

  const scopeFilter = useMemo<QuestionLogFilterInput>(() => {
    switch (scope) {
      case QUESTION_LOG_REVIEW_STATUS.Unreviewed:
        return {
          reviewStatus: [QUESTION_LOG_REVIEW_STATUS.Unreviewed],
        };
      case QUESTION_LOG_REVIEW_STATUS.Reviewed:
        return {
          reviewStatus: [QUESTION_LOG_REVIEW_STATUS.Reviewed],
        };
      case 'markedForReview':
        return { markedForReview: true };
      case 'starred':
        return { starred: true };
      default:
        return {};
    }
  }, [scope]);

  const nextActiveItemKeyRef = useRef<string>();
  const mutationVersionRef = useRef<Record<string, number>>({});
  const loadedPageCountRef = useRef(0);

  const loadQuestionLog = useCallback(
    async ({
      reset,
      pageStart = 1,
      pageLimit = QUESTION_LOG_AUTOLOAD_PAGE_LIMIT,
    }: {
      reset: boolean;
      pageStart?: number;
      pageLimit?: number;
    }) => {
      if (reset) {
        setIsQuestionLogLoading(true);
      } else {
        setIsQuestionLogRefreshing(true);
      }

      try {
        const firstPage = await client.query<
          GetQuestionLogListQuery,
          GetQuestionLogListQueryVariables
        >({
          query: GET_QUESTION_LOG_LIST,
          variables: {
            paging: {
              page: pageStart,
              limit: QUESTION_LOG_PAGE_SIZE,
            },
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all',
        });

        const firstResults =
          firstPage.data?.questionLog?.getQuestionLog?.results ?? [];
        const total = firstPage.data?.questionLog?.getQuestionLog?.total ?? 0;
        const totalPages = total
          ? Math.ceil(total / QUESTION_LOG_PAGE_SIZE)
          : firstResults.length
            ? 1
            : 0;
        const maxPageToLoad = Math.min(
          totalPages || pageStart,
          pageStart + pageLimit - 1,
        );

        const remainingPages =
          maxPageToLoad > pageStart
            ? await Promise.all(
                Array.from(
                  { length: maxPageToLoad - pageStart },
                  (_, index) =>
                    client.query<
                      GetQuestionLogListQuery,
                      GetQuestionLogListQueryVariables
                    >({
                      query: GET_QUESTION_LOG_LIST,
                      variables: {
                        paging: {
                          page: pageStart + index + 1,
                          limit: QUESTION_LOG_PAGE_SIZE,
                        },
                      },
                      fetchPolicy: 'network-only',
                      errorPolicy: 'all',
                    }),
                ),
              )
            : [];

        const mergedEntries = mergeQuestionLogEntries([
          ...firstResults,
          ...remainingPages.flatMap(
            (pageResult) =>
              pageResult.data?.questionLog?.getQuestionLog?.results ?? [],
          ),
        ]);

        setTotalPageCount(totalPages);
        setLoadedPageCount(maxPageToLoad);
        setServerEntries((currentEntries) =>
          reset
            ? mergedEntries
            : mergeQuestionLogEntries([...currentEntries, ...mergedEntries]),
        );
      } catch {
        toast.error('Unable to refresh Questions Log right now.');
      } finally {
        setIsQuestionLogLoading(false);
        setIsQuestionLogRefreshing(false);
      }
    },
    [client],
  );

  useEffect(() => {
    loadedPageCountRef.current = loadedPageCount;
  }, [loadedPageCount]);

  useEffect(() => {
    return subscribeQuestionLogStatusPatched(({ questionId, ...patch }) => {
      setExternalQuestionOverrides((current) => ({
        ...current,
        [questionId]: {
          ...current[questionId],
          ...patch,
        },
      }));
    });
  }, []);

  useEffect(() => {
    void loadQuestionLog({ reset: true });
  }, [loadQuestionLog]);

  useEffect(() => {
    const onWindowFocus = () => {
      void loadQuestionLog({
        reset: true,
        pageLimit: Math.max(
          loadedPageCountRef.current,
          QUESTION_LOG_AUTOLOAD_PAGE_LIMIT,
        ),
      });
    };

    window.addEventListener('focus', onWindowFocus);

    return () => {
      window.removeEventListener('focus', onWindowFocus);
    };
  }, [loadQuestionLog]);

  const [markReviewed] = useMutation(MARK_QUESTION_LOG_REVIEWED);
  const [markUnreviewed] = useMutation(MARK_QUESTION_LOG_UNREVIEWED);
  const [markOccurrenceReviewed] = useMutation(
    MARK_QUESTION_LOG_OCCURRENCE_REVIEWED,
  );
  const [markOccurrenceUnreviewed] = useMutation(
    MARK_QUESTION_LOG_OCCURRENCE_UNREVIEWED,
  );
  const [toggleStar] = useMutation(TOGGLE_QUESTION_LOG_STAR);
  const [toggleMarkedForReview] = useMutation(
    TOGGLE_QUESTION_LOG_MARKED_FOR_REVIEW,
  );

  const rawEntries = useMemo(
    () =>
      serverEntries
        .map((entry) => {
          const externalOverride = externalQuestionOverrides[entry.questionId];
          const override = entryOverrides[entry.itemKey];

          return {
            ...entry,
            ...externalOverride,
            ...override,
          };
        })
        .filter(isVisibleQuestionLogEntry),
    [entryOverrides, externalQuestionOverrides, serverEntries],
  );
  const scopedEntries = useMemo(
    () => applyScopeToEntries(rawEntries, scope),
    [rawEntries, scope],
  );
  const summaryEntries = useMemo(
    () =>
      applyTaxonomyFilter(scopedEntries, {
        ...taxonomyFilter,
        sectionIds: undefined,
      }),
    [scopedEntries, taxonomyFilter],
  );
  const entries = useMemo(
    () => applyTaxonomyFilter(scopedEntries, taxonomyFilter),
    [scopedEntries, taxonomyFilter],
  );

  const groups = useMemo(() => {
    const grouped = new Map<string, QuestionLogGroup>();

    entries.forEach((entry) => {
      const baseKey = `${entry.lastSourceType ?? 'manual'}-${entry.lastAttemptId ?? entry.itemKey}-${entry.lastAttemptCompletedAt ?? entry.starredAt ?? entry.itemKey}`;

      if (!grouped.has(baseKey)) {
        grouped.set(baseKey, {
          key: baseKey,
          title: entry.lastSourceName || 'Starred Questions',
          items: [],
        });
      }

      grouped.get(baseKey)?.items.push(entry);
    });

    return Array.from(grouped.values()).map((group) => ({
      ...group,
      items: [...group.items].sort((a, b) => {
        if (
          (a.lastSectionOrder ?? Number.MAX_SAFE_INTEGER) !==
          (b.lastSectionOrder ?? Number.MAX_SAFE_INTEGER)
        ) {
          return (
            (a.lastSectionOrder ?? Number.MAX_SAFE_INTEGER) -
            (b.lastSectionOrder ?? Number.MAX_SAFE_INTEGER)
          );
        }

        if (
          (a.lastModuleOrder ?? Number.MAX_SAFE_INTEGER) !==
          (b.lastModuleOrder ?? Number.MAX_SAFE_INTEGER)
        ) {
          return (
            (a.lastModuleOrder ?? Number.MAX_SAFE_INTEGER) -
            (b.lastModuleOrder ?? Number.MAX_SAFE_INTEGER)
          );
        }

        if (
          (a.lastQuestionOrder ?? Number.MAX_SAFE_INTEGER) !==
          (b.lastQuestionOrder ?? Number.MAX_SAFE_INTEGER)
        ) {
          return (
            (a.lastQuestionOrder ?? Number.MAX_SAFE_INTEGER) -
            (b.lastQuestionOrder ?? Number.MAX_SAFE_INTEGER)
          );
        }

        return a.itemKey.localeCompare(b.itemKey);
      }),
    }));
  }, [entries]);

  const groupAttemptLabels = useMemo(() => {
    const labels: Record<string, string> = {};
    const groupedBySource = new Map<string, QuestionLogGroup[]>();

    groups.forEach((group) => {
      const representative = group.items[0];

      if (
        !representative?.lastSourceType ||
        !representative?.lastSourceName ||
        !representative?.lastAttemptCompletedAt
      ) {
        return;
      }

      const sourceKey = `${representative.lastSourceType}-${representative.lastSourceName}`;
      const existingGroups = groupedBySource.get(sourceKey) ?? [];
      existingGroups.push(group);
      groupedBySource.set(sourceKey, existingGroups);
    });

    groupedBySource.forEach((sourceGroups) => {
      if (sourceGroups.length <= 1) {
        return;
      }

      const sortedGroups = [...sourceGroups].sort((a, b) => {
        const aDate = new Date(
          a.items[0]?.lastAttemptCompletedAt ?? 0,
        ).getTime();
        const bDate = new Date(
          b.items[0]?.lastAttemptCompletedAt ?? 0,
        ).getTime();

        return aDate - bDate;
      });

      sortedGroups.forEach((group, index) => {
        const ordinal = index + 1;

        if (ordinal > 1) {
          labels[group.key] = formatAttemptOrdinal(ordinal);
        }
      });
    });

    return labels;
  }, [groups]);

  const reviewEntries = useMemo(() => {
    const entryMap = new Map(rawEntries.map((entry) => [entry.itemKey, entry]));

    return reviewQueueEntries.map((entry) => {
      const currentEntry = entryMap.get(entry.itemKey) ?? entry;
      const externalOverride = externalQuestionOverrides[entry.questionId];
      const override = entryOverrides[entry.itemKey];

      return {
        ...currentEntry,
        ...externalOverride,
        ...override,
      };
    });
  }, [entryOverrides, externalQuestionOverrides, rawEntries, reviewQueueEntries]);

  useEffect(() => {
    setExternalQuestionOverrides((current) => {
      let hasChanges = false;
      const next = { ...current };

      Object.entries(current).forEach(([questionId, patch]) => {
        const numericQuestionId = Number(questionId);
        const matchingEntries = serverEntries.filter(
          (entry) => entry.questionId === numericQuestionId,
        );

        if (!matchingEntries.length) {
          return;
        }

        const patchIsReflected = matchingEntries.every((entry) =>
          Object.entries(patch).every(
            ([key, value]) =>
              String(entry[key as keyof QuestionLogEntry] ?? '') ===
              String(value ?? ''),
          ),
        );

        if (!patchIsReflected) {
          return;
        }

        clearQuestionLogStatusPatch(numericQuestionId);
        delete next[numericQuestionId];
        hasChanges = true;
      });

      return hasChanges ? next : current;
    });
  }, [serverEntries]);

  const activeIndex = useMemo(
    () => reviewEntries.findIndex((entry) => entry.itemKey === activeItemKey),
    [activeItemKey, reviewEntries],
  );
  const activeEntry = activeIndex >= 0 ? reviewEntries[activeIndex] : undefined;

  useEffect(() => {
    if (!activeItemKey) {
      return;
    }

    if (reviewEntries.some((entry) => entry.itemKey === activeItemKey)) {
      return;
    }

    const fallbackEntryId = nextActiveItemKeyRef.current;
    nextActiveItemKeyRef.current = undefined;

    if (
      fallbackEntryId &&
      reviewEntries.some((entry) => entry.itemKey === fallbackEntryId)
    ) {
      setActiveItemKey(fallbackEntryId);
      return;
    }

    setActiveItemKey(undefined);
  }, [activeItemKey, reviewEntries]);

  const sectionCards = useMemo(
    () => {
      const sectionMap = new Map<
        number,
        { id: number; name: string; count: number }
      >();

      summaryEntries.forEach((entry) => {
        const section = entry.question.section;

        if (!section) {
          return;
        }

        if (!sectionMap.has(section.id)) {
          sectionMap.set(section.id, {
            id: section.id,
            name: section.name,
            count: 0,
          });
        }
      });

      summaryEntries.forEach((entry) => {
        const section = entry.question.section;

        if (!section) {
          return;
        }

        const current = sectionMap.get(section.id);

        if (current) {
          current.count += 1;
        }
      });

      return sortSections(Array.from(sectionMap.values()));
    },
    [summaryEntries],
  );

  const facetLookup = useMemo(() => {
    const sectionMap: Record<number, string> = {};
    const domainMap: Record<number, string> = {};
    const skillMap: Record<number, string> = {};
    const qtypeMap: Record<number, string> = {};

    rawEntries.forEach((entry) => {
      if (entry.question.section?.id) {
        sectionMap[entry.question.section.id] = entry.question.section.name;
      }
      if (entry.question.domain?.id) {
        domainMap[entry.question.domain.id] = entry.question.domain.name;
      }
      if (entry.question.skill?.id) {
        skillMap[entry.question.skill.id] = entry.question.skill.name;
      }
      if (entry.question.type1?.id) {
        qtypeMap[entry.question.type1.id] = entry.question.type1.name;
      }
      if (entry.question.type2?.id) {
        qtypeMap[entry.question.type2.id] = entry.question.type2.name;
      }
    });

    return { sectionMap, domainMap, skillMap, qtypeMap };
  }, [rawEntries]);

  const clearEntryOverride = (itemKey: string) => {
    setEntryOverrides((current) => {
      if (!current[itemKey]) {
        return current;
      }

      const next = { ...current };
      delete next[itemKey];
      return next;
    });
  };

  const clearEntryOverridesForQuestion = (questionId: number) => {
    setEntryOverrides((current) => {
      const nextEntries = Object.entries(current).filter(([itemKey]) => {
        const serverEntry = serverEntries.find(
          (currentEntry) => currentEntry.itemKey === itemKey,
        );

        return serverEntry?.questionId !== questionId;
      });

      if (nextEntries.length === Object.keys(current).length) {
        return current;
      }

      return Object.fromEntries(nextEntries);
    });
  };

  const clearExternalQuestionOverrideFields = (
    questionId: number,
    fields: Array<keyof QuestionLogEntryOverride>,
  ) => {
    setExternalQuestionOverrides((current) => {
      const currentPatch = current[questionId];

      if (!currentPatch) {
        return current;
      }

      const nextPatch = { ...currentPatch };

      fields.forEach((field) => {
        delete nextPatch[field];
      });

      if (Object.keys(nextPatch).length === 0) {
        const next = { ...current };
        delete next[questionId];
        return next;
      }

      return {
        ...current,
        [questionId]: nextPatch,
      };
    });
  };

  const addPendingEntry = (itemKey: string) => {
    setPendingItemKeys((current) =>
      current.includes(itemKey) ? current : [...current, itemKey],
    );
  };

  const removePendingEntry = (entryId: string) => {
    setPendingItemKeys((current) =>
      current.filter((currentEntryId) => currentEntryId !== entryId),
    );
  };

  const addPendingQuestion = (questionId: number) => {
    setPendingQuestionIds((current) =>
      current.includes(questionId) ? current : [...current, questionId],
    );
  };

  const removePendingQuestion = (questionId: number) => {
    setPendingQuestionIds((current) =>
      current.filter((currentQuestionId) => currentQuestionId !== questionId),
    );
  };

  const isEntrySaving = (entry: QuestionLogEntry) =>
    pendingItemKeys.includes(entry.itemKey) ||
    pendingQuestionIds.includes(entry.questionId);

  const getFallbackEntryId = (itemKey?: string) => {
    if (!itemKey) {
      return undefined;
    }

    const currentIndex = reviewEntries.findIndex(
      (entry) => entry.itemKey === itemKey,
    );

    if (currentIndex < 0) {
      return undefined;
    }

    return (
      reviewEntries[currentIndex + 1]?.itemKey ??
      reviewEntries[currentIndex - 1]?.itemKey
    );
  };

  const onApplyScope = (nextScope: ScopeKey) => {
    setScope(nextScope);
  };

  const patchServerEntry = (
    itemKey: string,
    updater: (entry: QuestionLogEntry) => QuestionLogEntry | undefined,
  ) => {
    setServerEntries((currentEntries) =>
      currentEntries.flatMap((entry) => {
        if (entry.itemKey !== itemKey) {
          return [entry];
        }

        const updatedEntry = updater(entry);
        return updatedEntry ? [updatedEntry] : [];
      }),
    );
  };

  const patchServerEntriesByQuestionId = (
    questionId: number,
    updater: (entry: QuestionLogEntry) => QuestionLogEntry | undefined,
  ) => {
    setServerEntries((currentEntries) =>
      currentEntries.flatMap((entry) => {
        if (entry.questionId !== questionId) {
          return [entry];
        }

        const updatedEntry = updater(entry);
        return updatedEntry ? [updatedEntry] : [];
      }),
    );
  };

  const patchReviewQueueEntry = (
    itemKey: string,
    updater: (entry: QuestionLogEntry) => QuestionLogEntry,
  ) => {
    setReviewQueueEntries((currentEntries) =>
      currentEntries.map((entry) =>
        entry.itemKey === itemKey ? updater(entry) : entry,
      ),
    );
  };

  const patchReviewQueueEntriesByQuestionId = (
    questionId: number,
    updater: (entry: QuestionLogEntry) => QuestionLogEntry,
  ) => {
    setReviewQueueEntries((currentEntries) =>
      currentEntries.map((entry) =>
        entry.questionId === questionId ? updater(entry) : entry,
      ),
    );
  };

  const startMutationVersion = (type: string, entityKey: string | number) => {
    const mutationKey = `${type}-${entityKey}`;
    const nextVersion = (mutationVersionRef.current[mutationKey] ?? 0) + 1;

    mutationVersionRef.current[mutationKey] = nextVersion;

    return { mutationKey, version: nextVersion };
  };

  const isLatestMutationVersion = (
    mutationKey: string,
    version: number,
  ) => mutationVersionRef.current[mutationKey] === version;

  const onToggleStar = (entry: QuestionLogEntry) => {
    const nextStarred = !entry.starred;
    clearQuestionLogStatusPatchFields(entry.questionId, ['starred', 'starredAt']);
    clearExternalQuestionOverrideFields(entry.questionId, ['starred', 'starredAt']);
    const { mutationKey, version } = startMutationVersion(
      'star',
      entry.questionId,
    );
    addPendingQuestion(entry.questionId);

    setEntryOverrides((current) => {
      const nextOverrides = { ...current };
      const nextStarredAt = nextStarred ? new Date().toISOString() : null;

      serverEntries.forEach((currentEntry) => {
        if (currentEntry.questionId !== entry.questionId) {
          return;
        }

        nextOverrides[currentEntry.itemKey] = {
          ...current[currentEntry.itemKey],
          starred: nextStarred,
          starredAt: nextStarredAt,
        };
      });

      return nextOverrides;
    });

    void (async () => {
      try {
        const result = await toggleStar({
          variables: {
            input: {
              questionId: entry.questionId,
              starred: nextStarred,
            },
          },
        });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry = result.data?.questionLog?.toggleStar;

        patchServerEntriesByQuestionId(entry.questionId, (currentEntry) => {
          if (!nextStarred && currentEntry.wrongCount === 0) {
            return undefined;
          }

          return {
            ...currentEntry,
            starred: nextStarred,
            starredAt:
              nextStarred ? (nextEntry?.starredAt ?? new Date().toISOString()) : null,
          };
        });
        patchReviewQueueEntriesByQuestionId(entry.questionId, (currentEntry) => ({
          ...currentEntry,
          starred: nextStarred,
          starredAt:
            nextStarred ? (nextEntry?.starredAt ?? new Date().toISOString()) : null,
        }));
        clearEntryOverridesForQuestion(entry.questionId);
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        clearEntryOverridesForQuestion(entry.questionId);
        toast.error('Unable to update the star right now. Please try again.');
      } finally {
        if (isLatestMutationVersion(mutationKey, version)) {
          removePendingQuestion(entry.questionId);
        }
      }
    })();
  };

  const onToggleMarkedForReview = (
    entry: QuestionLogEntry,
    nextMarkedForReview = !entry.markedForReview,
  ) => {
    clearQuestionLogStatusPatchFields(entry.questionId, [
      'markedForReview',
      'markedForReviewAt',
    ]);
    clearExternalQuestionOverrideFields(entry.questionId, [
      'markedForReview',
      'markedForReviewAt',
    ]);
    const { mutationKey, version } = startMutationVersion(
      'markedForReview',
      entry.questionId,
    );
    addPendingQuestion(entry.questionId);

    setEntryOverrides((current) => {
      const nextOverrides = { ...current };
      const nextMarkedForReviewAt = nextMarkedForReview
        ? new Date().toISOString()
        : null;

      serverEntries.forEach((currentEntry) => {
        if (currentEntry.questionId !== entry.questionId) {
          return;
        }

        nextOverrides[currentEntry.itemKey] = {
          ...current[currentEntry.itemKey],
          markedForReview: nextMarkedForReview,
          markedForReviewAt: nextMarkedForReviewAt,
        };
      });

      return nextOverrides;
    });

    void (async () => {
      try {
        const result = await toggleMarkedForReview({
          variables: {
            input: {
              questionId: entry.questionId,
              markedForReview: nextMarkedForReview,
            },
          },
        });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry = result.data?.questionLog?.toggleMarkedForReview;

        patchServerEntriesByQuestionId(entry.questionId, (currentEntry) => ({
          ...currentEntry,
          markedForReview: nextMarkedForReview,
          markedForReviewAt: nextMarkedForReview
            ? (nextEntry?.markedForReviewAt ?? new Date().toISOString())
            : null,
        }));
        patchReviewQueueEntriesByQuestionId(entry.questionId, (currentEntry) => ({
          ...currentEntry,
          markedForReview: nextMarkedForReview,
          markedForReviewAt: nextMarkedForReview
            ? (nextEntry?.markedForReviewAt ?? new Date().toISOString())
            : null,
        }));
        clearEntryOverridesForQuestion(entry.questionId);
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        clearEntryOverridesForQuestion(entry.questionId);
        toast.error(
          'Unable to update Marked for Review right now. Please try again.',
        );
      } finally {
        if (isLatestMutationVersion(mutationKey, version)) {
          removePendingQuestion(entry.questionId);
        }
      }
    })();
  };

  const onSetReviewStatus = (
    entry: QuestionLogEntry,
    reviewStatus: QuestionLogEntry['reviewStatus'],
    nextActiveEntryId?: string,
  ) => {
    nextActiveItemKeyRef.current = nextActiveEntryId;

    const nextReviewedAt =
      reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
        ? new Date().toISOString()
        : null;
    const { mutationKey, version } = startMutationVersion(
      'review',
      entry.occurrenceId ?? entry.itemKey,
    );
    addPendingEntry(entry.itemKey);

    setEntryOverrides((current) => ({
      ...current,
      [entry.itemKey]: {
        ...current[entry.itemKey],
        reviewStatus,
        reviewedAt: nextReviewedAt,
      },
    }));

    void (async () => {
      try {
        const result =
          entry.occurrenceId
            ? reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? await markOccurrenceReviewed({
                  variables: {
                    input: {
                      occurrenceId: entry.occurrenceId,
                    },
                  },
                })
              : await markOccurrenceUnreviewed({
                  variables: {
                    input: {
                      occurrenceId: entry.occurrenceId,
                    },
                  },
                })
            : reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? await markReviewed({
                  variables: {
                    input: { questionId: entry.questionId },
                  },
                })
              : await markUnreviewed({
                  variables: {
                    input: { questionId: entry.questionId },
                  },
                });

        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        const nextEntry =
          entry.occurrenceId
            ? reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? result.data?.questionLog?.markOccurrenceReviewed
              : result.data?.questionLog?.markOccurrenceUnreviewed
            : reviewStatus === QUESTION_LOG_REVIEW_STATUS.Reviewed
              ? result.data?.questionLog?.markReviewed
              : result.data?.questionLog?.markUnreviewed;

        patchServerEntry(entry.itemKey, (currentEntry) => ({
          ...currentEntry,
          reviewStatus,
          reviewedAt: nextEntry?.reviewedAt ?? nextReviewedAt,
        }));
        patchReviewQueueEntry(entry.itemKey, (currentEntry) => ({
          ...currentEntry,
          reviewStatus,
          reviewedAt: nextEntry?.reviewedAt ?? nextReviewedAt,
        }));
        clearEntryOverride(entry.itemKey);
      } catch {
        if (!isLatestMutationVersion(mutationKey, version)) {
          return;
        }

        clearEntryOverride(entry.itemKey);
        toast.error('Unable to update the review status right now.');
      } finally {
        if (isLatestMutationVersion(mutationKey, version)) {
          removePendingEntry(entry.itemKey);
        }
      }
    })();
  };

  const onApplySection = (sectionId: number) => {
    setTaxonomyFilter((current) => ({
      ...current,
      sectionIds: current.sectionIds?.includes(sectionId)
        ? undefined
        : [sectionId],
      domainIds: undefined,
      skillIds: undefined,
      qtypeIds: undefined,
    }));
  };

  const onApplyQuickFilter = ({
    sectionId,
    domainId,
    skillId,
    qtypeId,
  }: QuestionLogQuickFilter) => {
    setTaxonomyFilter({
      sectionIds: sectionId ? [sectionId] : undefined,
      domainIds: domainId ? [domainId] : undefined,
      skillIds: skillId ? [skillId] : undefined,
      qtypeIds: qtypeId ? [qtypeId] : undefined,
    });
  };

  return (
    <section className="mx-auto w-full max-w-[72rem] pb-10">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1>Questions Log</h1>
          <p className="description mt-2">
            Review every SAT question you missed, star the ones you want to
            revisit, and filter by section, domain, skill, or qtype.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            outlined
            tip={isQuestionLogRefreshing ? 'Refreshing Questions Log' : 'Refresh Questions Log'}
            className="!border-[#D7E3F2] !bg-white !text-dark-blue hover:!border-blue hover:!bg-light-blue hover:!text-dark-blue"
            onClick={() =>
              void loadQuestionLog({
                reset: true,
                pageLimit: Math.max(
                  loadedPageCount || 1,
                  QUESTION_LOG_AUTOLOAD_PAGE_LIMIT,
                ),
              })
            }
            disabled={isQuestionLogLoading || isQuestionLogRefreshing}
          >
            <span className="sr-only">
              {isQuestionLogRefreshing ? 'Refreshing Questions Log' : 'Refresh Questions Log'}
            </span>
            <span className="inline-flex items-center">
              <ArrowPathIcon
                className={cn(
                  'h-4 w-4',
                  isQuestionLogRefreshing && 'animate-spin',
                )}
              />
            </span>
          </Button>
          <Button white onClick={() => setIsFilterModalOpen(true)}>
            Advanced Filters
          </Button>
          <Button
            outlined
            className="!border-[#D7E3F2] !bg-white !text-dark-blue hover:!border-blue hover:!bg-light-blue hover:!text-dark-blue"
            onClick={() => {
              setScope('all');
              setTaxonomyFilter({});
            }}
          >
            Clear All
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2">
        {scopeOptions.map((option) => {
          const selected = scope === option.key;

          return (
            <button
              key={option.key}
              onClick={() => onApplyScope(option.key)}
              className={getQuickFilterButtonClass(selected)}
            >
              {renderScopeIcon(option.key)}
              {option.label}
            </button>
          );
        })}
      </div>

      <div className="mt-3 grid gap-2 md:grid-cols-2">
        {sectionCards.map((section) => {
          const selected = taxonomyFilter.sectionIds?.includes(section.id);
          const sectionTheme = getSectionTheme(section.name);

          return (
            <button
              key={section.id}
              onClick={() => onApplySection(section.id)}
              className={cn(
                'rounded-xl border px-3 py-2 text-left transition-colors',
                selected ? sectionTheme.cardSelected : sectionTheme.cardDefault,
              )}
            >
              <div className="text-sm font-semibold">
                {capitalizeWords(section.name)}: {section.count} question
                {section.count === 1 ? '' : 's'}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-1.5 flex flex-wrap gap-2">
        {taxonomyFilter.sectionIds?.map((id) => (
          <QuestionLogSelectedFilterChip
            key={`section-${id}`}
            title={capitalizeWords(facetLookup.sectionMap[id] || `Section ${id}`)}
            color={getSectionTheme(facetLookup.sectionMap[id]).chipColor}
            onRemove={() =>
              setTaxonomyFilter((current) => ({
                ...current,
                sectionIds: current.sectionIds?.filter(
                  (currentId) => currentId !== id,
                ),
                domainIds: undefined,
                skillIds: undefined,
                qtypeIds: undefined,
              }))
            }
          />
        ))}
        {taxonomyFilter.domainIds?.map((id) => (
          <QuestionLogSelectedFilterChip
            key={`domain-${id}`}
            title={capitalizeWords(facetLookup.domainMap[id] || `Domain ${id}`)}
            onRemove={() =>
              setTaxonomyFilter((current) => ({
                ...current,
                domainIds: current.domainIds?.filter(
                  (currentId) => currentId !== id,
                ),
                skillIds: undefined,
                qtypeIds: undefined,
              }))
            }
          />
        ))}
        {taxonomyFilter.skillIds?.map((id) => (
          <QuestionLogSelectedFilterChip
            key={`skill-${id}`}
            title={capitalizeWords(facetLookup.skillMap[id] || `Skill ${id}`)}
            onRemove={() =>
              setTaxonomyFilter((current) => ({
                ...current,
                skillIds: current.skillIds?.filter(
                  (currentId) => currentId !== id,
                ),
                qtypeIds: undefined,
              }))
            }
          />
        ))}
        {taxonomyFilter.qtypeIds?.map((id) => (
          <QuestionLogSelectedFilterChip
            key={`qtype-${id}`}
            title={capitalizeWords(facetLookup.qtypeMap[id] || `Qtype ${id}`)}
            onRemove={() =>
              setTaxonomyFilter((current) => ({
                ...current,
                qtypeIds: current.qtypeIds?.filter(
                  (currentId) => currentId !== id,
                ),
              }))
            }
          />
        ))}
      </div>

      <div className="mt-3">
        {isQuestionLogLoading && !entries.length ? (
          <div className="flex justify-center py-20">
            <Loading />
          </div>
        ) : !entries.length ? (
          <div className="rounded-3xl border border-table-border bg-white px-6 py-12 text-center">
            <div className="text-xl font-semibold text-dark-blue">
              No questions in your log yet
            </div>
            <p className="description mt-3">
              Questions you miss on quizzes and tests will show up here
              automatically.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {groups.map((group) => {
              const reviewableGroupEntries = group.items.filter(
                (entry) =>
                  entry.reviewStatus !== QUESTION_LOG_REVIEW_STATUS.Reviewed,
              );
              const isGroupSaving = reviewableGroupEntries.some((entry) =>
                isEntrySaving(entry),
              );

              return (
                <section
                  key={group.key}
                  className="rounded-2xl border border-table-border bg-white p-2.5"
                >
                  <div className="px-0.5 pb-1.5">
                    <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
                      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                        <div className="truncate text-sm font-semibold text-black">
                          {group.title}
                        </div>
                        {group.items[0]?.lastAttemptCompletedAt && (
                          <div className="text-xs text-gray">
                            Latest miss:{' '}
                            <LocalDate
                              date={group.items[0].lastAttemptCompletedAt}
                              format="MMM d, yyyy 'at' h:mm a"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center justify-end gap-3">
                        <button
                          type="button"
                          className={questionLogGroupActionClass}
                          disabled={!reviewableGroupEntries.length || isGroupSaving}
                          onClick={() => {
                            reviewableGroupEntries.forEach((entry) => {
                              onSetReviewStatus(
                                entry,
                                QUESTION_LOG_REVIEW_STATUS.Reviewed,
                              );
                            });
                          }}
                        >
                          {isGroupSaving ? 'Saving...' : 'Mark All as Done'}
                        </button>
                        <Button
                          className="shrink-0 px-3 py-1.5 text-sm"
                          onClick={() => {
                            setReviewQueueEntries(group.items);
                            setActiveItemKey(group.items[0]?.itemKey);
                          }}
                        >
                          Review All
                        </Button>
                      </div>
                    </div>
                  </div>

                  <div className="mt-2 space-y-1.5">
                    {group.items.map((entry) => (
                      <QuestionLogEntryRow
                        key={entry.itemKey}
                        entry={entry}
                        attemptLabel={groupAttemptLabels[group.key]}
                        isSaving={isEntrySaving(entry)}
                        onOpenReview={(itemKey) => {
                          setReviewQueueEntries(entries);
                          setActiveItemKey(itemKey);
                        }}
                        onApplyQuickFilter={onApplyQuickFilter}
                        onReview={(currentEntry) =>
                          onSetReviewStatus(
                            currentEntry,
                            QUESTION_LOG_REVIEW_STATUS.Reviewed,
                          )
                        }
                        onToggleMarkedForReview={onToggleMarkedForReview}
                        onToggleStar={onToggleStar}
                      />
                    ))}
                  </div>
                </section>
              );
            })}

            {loadedPageCount < totalPageCount && (
              <div className="flex justify-center pt-2">
                <Button
                  outlined
                  onClick={() =>
                    void loadQuestionLog({
                      reset: false,
                      pageStart: loadedPageCount + 1,
                    })
                  }
                  disabled={isQuestionLogRefreshing}
                >
                  {isQuestionLogRefreshing ? 'Loading More...' : 'Load More History'}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      <QuestionLogFilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filter={taxonomyFilter}
        scopeFilter={scopeFilter}
        onApply={(nextFilter) => {
          setTaxonomyFilter(nextFilter);
        }}
      />

      <QuestionLogReviewModal
        entry={activeEntry}
        isOpen={!!activeEntry}
        onClose={() => {
          setActiveItemKey(undefined);
          setReviewQueueEntries([]);
        }}
        onNext={() => {
          if (activeIndex < reviewEntries.length - 1) {
            setActiveItemKey(reviewEntries[activeIndex + 1].itemKey);
          }
        }}
        onPrev={() => {
          if (activeIndex > 0) {
            setActiveItemKey(reviewEntries[activeIndex - 1].itemKey);
          }
        }}
        nextDisabled={activeIndex >= reviewEntries.length - 1}
        prevDisabled={activeIndex <= 0}
        onMarkReviewed={() =>
          onSetReviewStatus(
            activeEntry!,
            QUESTION_LOG_REVIEW_STATUS.Reviewed,
            getFallbackEntryId(activeEntry?.itemKey),
          )
        }
        onMarkUnreviewed={() =>
          onSetReviewStatus(
            activeEntry!,
            QUESTION_LOG_REVIEW_STATUS.Unreviewed,
            getFallbackEntryId(activeEntry?.itemKey),
          )
        }
        onToggleStar={() =>
          onToggleStar(activeEntry!)
        }
        onToggleMarkedForReview={(mark) =>
          onToggleMarkedForReview(activeEntry!, mark)
        }
        isSaving={!!activeEntry && isEntrySaving(activeEntry)}
      />
    </section>
  );
};

export default QuestionsLog;
